import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { comics, categories } from '@/mocks/comics';

const categoryHeroImages: Record<string, string> = {
  romance: 'https://readdy.ai/api/search-image?query=Artistic%20abstract%20romantic%20style%20illustration%2C%20warm%20pink%20and%20peach%20ink%20wash%20background%2C%20soft%20flowing%20lines%2C%20elegant%20minimal%20texture%2C%20subtle%20heart%20motifs%2C%20no%20characters%2C%20dreamy%20atmosphere&width=1800&height=600&seq=category-hero-romance&orientation=landscape',
  fantasy: 'https://readdy.ai/api/search-image?query=Artistic%20fantasy%20style%20illustration%2C%20magical%20golden%20and%20emerald%20ink%20wash%20background%2C%20flowing%20mystical%20lines%2C%20elegant%20magic%20texture%2C%20subtle%20sparkle%20elements%2C%20no%20characters%2C%20enchanting%20atmosphere&width=1800&height=600&seq=category-hero-fantasy&orientation=landscape',
  action: 'https://readdy.ai/api/search-image?query=Artistic%20dynamic%20action%20style%20illustration%2C%20bold%20red%20and%20charcoal%20ink%20wash%20background%2C%20energetic%20flowing%20brush%20strokes%2C%20dramatic%20texture%2C%20subtle%20speed%20line%20elements%2C%20no%20characters%2C%20intense%20atmosphere&width=1800&height=600&seq=category-hero-action&orientation=landscape',
  suspense: 'https://readdy.ai/api/search-image?query=Artistic%20mysterious%20noir%20style%20illustration%2C%20dark%20indigo%20and%20gray%20ink%20wash%20background%2C%20shadowy%20flowing%20lines%2C%20ominous%20texture%2C%20subtle%20fog%20elements%2C%20no%20characters%2C%20suspenseful%20atmosphere&width=1800&height=600&seq=category-hero-suspense&orientation=landscape',
  comedy: 'https://readdy.ai/api/search-image?query=Artistic%20playful%20colorful%20style%20illustration%2C%20bright%20yellow%20and%20orange%20ink%20wash%20background%2C%20bouncy%20flowing%20lines%2C%20cheerful%20texture%2C%20subtle%20pop%20art%20elements%2C%20no%20characters%2C%20fun%20atmosphere&width=1800&height=600&seq=category-hero-comedy&orientation=landscape',
  drama: 'https://readdy.ai/api/search-image?query=Artistic%20emotional%20dramatic%20style%20illustration%2C%20deep%20burgundy%20and%20brown%20ink%20wash%20background%2C%20sweeping%20flowing%20lines%2C%20rich%20texture%2C%20subtle%20theatrical%20elements%2C%20no%20characters%2C%20poignant%20atmosphere&width=1800&height=600&seq=category-hero-drama&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);
  const categoryComics = category
    ? comics.filter((c) => c.categorySlug === slug)
    : [];

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="mt-4 font-heading text-xl font-bold text-foreground-800">分类未找到</h2>
            <p className="mt-2 text-sm text-foreground-500">请返回首页浏览其他分类</p>
            <Link
              to="/"
              className="mt-6 inline-block px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.categorySlug !== slug)
    .slice(0, 4);

  const finalTitle = category.name + ' - 虚空韩漫网 | 免费在线韩国漫画';
  const finalDescription = '虚空韩漫网' + category.name + '专区，' + category.description + '。高清画质，免费在线阅读，每日更新热门' + category.name + '。';
  const finalKeywords = category.name + ',韩国漫画,免费韩漫,在线漫画,虚空韩漫网';

  const heroImage = categoryHeroImages[slug || ''] || categoryHeroImages.romance;

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category.name + ' - 虚空韩漫网',
    description: category.description,
    url: 'http://txtjly.com/category/' + slug,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryComics.map((comic, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Book',
          name: comic.title,
          author: { '@type': 'Person', name: comic.author },
          url: 'http://txtjly.com/article/' + comic.id,
        },
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={finalTitle}
        description={finalDescription}
        keywords={finalKeywords}
        schema={categorySchema}
      />

      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={[
            { label: '首页', path: '/' },
            { label: category.name },
          ]} />
        </div>

        <section className="relative w-full h-[360px] md:h-[440px] flex items-center overflow-hidden">
          <img
            src={heroImage}
            alt={category.name + ' - 虚空韩漫网'}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>

          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 animate-fade-in-up">
                {category.name}
              </h1>
              <p className="mt-4 text-sm md:text-base text-background-50/70 max-w-2xl mx-auto leading-relaxed animate-fade-in-up animation-delay-100">
                {category.description}
              </p>
              <p className="mt-6 text-xs text-background-50/50 animate-fade-in-up animation-delay-200">
                共 {categoryComics.length} 部作品
              </p>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-foreground-950">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-10">
              <span className="px-4 py-1.5 border border-background-50/30 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                分类精选
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50">
                {category.name}
              </h2>
            </div>

            {categoryComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                {categoryComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} variant="compact" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-50/10">
                  <i className="ri-book-2-line text-2xl text-background-50/50"></i>
                </div>
                <p className="mt-4 text-background-50/50 text-sm">该分类暂无漫画，敬请期待</p>
              </div>
            )}
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
              没找到心仪的漫画？
            </h2>
            <p className="mt-3 text-sm text-foreground-500">
              浏览其他分类，发现更多精彩韩漫
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              {categories.filter(c => c.slug !== slug).map((c) => (
                <Link
                  key={c.slug}
                  to={'/category/' + c.slug}
                  className="px-5 py-2.5 bg-secondary-100 text-secondary-800 rounded-full text-sm font-medium hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-10">
              猜你喜欢
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {relatedComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} variant="compact" />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}